import { FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProfitIcon, LossIcon } from '../../../assets/icons';
import { AppContext } from '../../../context/ContextProvider';
import { roundNumber } from '../../../utils';
import { CellarStats } from '../hooks/useCellarStats';

interface CellarSummaryProps {
  stats: CellarStats;
  loading?: boolean;
}

const CellarSummary: FC<CellarSummaryProps> = ({ stats, loading }) => {
  const { t } = useTranslation();
  const { formatter } = useContext(AppContext);

  const formattedStats = useMemo(() => {
    const pnlPrefix = stats.profitAndLoss >= 0 ? '+' : '-';
    // Values are already in the customer's target currency (sourced from current holdings, same
    // as the Portfolio "Portfolio Valuation"), so format with the target-currency symbol only —
    // no FX conversion (matches the Portfolio Details totals).
    return {
      totalValue: formatter.format(stats.totalValue, true),
      profitAndLoss: formatter.format(Math.abs(stats.profitAndLoss), true),
      profitAndLossPercentage: `${stats.profitAndLossPercentage >= 0 ? '+' : ''}${roundNumber(
        stats.profitAndLossPercentage,
        1,
      )}%`,
      pnlPrefix,
      trend: stats.profitAndLoss >= 0 ? 'up' : 'down',
    };
  }, [stats, formatter]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        {/* Hero skeleton */}
        <div className="bg-gradient-to-br from-vine to-vine/80 rounded-2xl p-5 animate-pulse">
          <div className="h-4 bg-white/20 rounded w-24 mb-2" />
          <div className="h-10 bg-white/20 rounded w-40 mb-3" />
          <div className="h-6 bg-white/20 rounded w-32" />
        </div>
        {/* Stats skeleton */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-100 dark:bg-slate-700 rounded-xl h-20 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Hero Card - Portfolio Value */}
      <div className="bg-gradient-to-br from-vine to-vine/80 rounded-2xl p-5 sm:p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Main Value */}
          <div>
            <p className="text-xs sm:text-sm text-white/70 uppercase tracking-wider font-medium">
              {t('common:totalValue')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold mt-1">{formattedStats.totalValue}</h2>
            <p className="text-sm text-white/60 mt-1">
              {stats.uniqueWines} {t('common:wines')}
            </p>
          </div>

          {/* P&L Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${
              formattedStats.trend === 'up' ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}
          >
            {formattedStats.trend === 'up' ? (
              <ProfitIcon className="w-4 h-4 text-green-300" />
            ) : (
              <LossIcon className="w-4 h-4 text-red-300" />
            )}
            <div className="text-right">
              <p className="text-xs text-white/60">{t('common:profitLoss')}</p>
              <p
                className={`text-base font-semibold ${
                  formattedStats.trend === 'up' ? 'text-green-200' : 'text-red-200'
                }`}
              >
                {formattedStats.pnlPrefix}
                {formattedStats.profitAndLoss}
                <span className="text-sm ml-1 opacity-80">({formattedStats.profitAndLossPercentage})</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        {/* Cases */}
        <div className="bg-white dark:bg-slate-700 rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-orange" />
            <span className="text-xs text-gray-500 dark:text-gray-400">{t('common:cases')}</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.cases.toLocaleString()}</p>
        </div>

        {/* Bottles */}
        <div className="bg-white dark:bg-slate-700 rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">{t('common:bottles')}</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {stats.bottles.toLocaleString()}
          </p>
        </div>

        {/* Regions */}
        <div className="bg-white dark:bg-slate-700 rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">{t('common:regionsStr')}</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {stats.regionAllocations.length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CellarSummary;
